import { useState, useEffect, useCallback, useMemo } from "react";
import { fetchAccounts, actOnAccount } from "../utils/auth";

// Admin-only review queue for account requests, and the roll of who already
// has access. Requesting an account grants nothing — an admin activates it
// here, and can suspend or demote afterwards.
const STATUS_STYLES = {
  pending:   "bg-amber-100 text-amber-800",
  active:    "bg-green-100 text-green-800",
  declined:  "bg-gray-200 text-gray-600",
  suspended: "bg-red-100 text-red-700",
};

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

export default function AccountsView({ currentUser, onNotify, onPendingChange }) {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setAccounts(await fetchAccounts());
      setError(null);
    } catch (e) {
      setAccounts([]);
      setError(e.message || "Could not load accounts.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { pending: 0, active: 0, declined: 0, suspended: 0 };
    (accounts || []).forEach(a => { if (c[a.status] !== undefined) c[a.status]++; });
    return c;
  }, [accounts]);

  // Keep the rail badge in step with the queue.
  useEffect(() => {
    if (accounts !== null) onPendingChange?.(counts.pending);
  }, [accounts, counts.pending, onPendingChange]);

  const visible = useMemo(
    () => (accounts || []).filter(a => a.status === filter),
    [accounts, filter]
  );

  async function act(account, action, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusyId(account.id);
    try {
      await actOnAccount(account.id, action);
      await load();
      onNotify?.({ message: `${account.email} — ${action.replace("-", " ")}d.` });
    } catch (e) {
      onNotify?.({ message: e.message || "That didn't work." });
    } finally {
      setBusyId(null);
    }
  }

  if (accounts === null) {
    return <p className="text-sm text-gray-500 p-6">Loading accounts…</p>;
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Accounts</h2>
          <p className="text-sm text-gray-500">
            Anyone can request an account. Nobody gets in until you activate them here.
          </p>
        </div>
        <button
          onClick={load}
          className="text-sm border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg transition-colors"
        >
          Refresh
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["pending", "active", "suspended", "declined"].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`text-sm px-3 py-1.5 rounded-lg border capitalize transition-colors ${
              filter === s
                ? "bg-blue-600 border-blue-600 text-white font-medium"
                : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {s} ({counts[s]})
          </button>
        ))}
      </div>

      {error && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          {error}
        </p>
      )}

      {visible.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
          <div className="text-3xl mb-2">👥</div>
          <p className="text-gray-500 text-sm">
            {filter === "pending" ? "No account requests waiting." : `No ${filter} accounts.`}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map(a => {
            const isSelf = a.id === currentUser?.id;
            const busy = busyId === a.id;
            return (
              <div
                key={a.id}
                className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-800">{a.name || "(no name)"}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_STYLES[a.status] || "bg-gray-100 text-gray-600"}`}>
                      {a.status}
                    </span>
                    {a.role === "admin" && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">admin</span>
                    )}
                    {isSelf && <span className="text-xs text-gray-400">you</span>}
                  </div>
                  <div className="text-sm text-gray-500 break-all">{a.email}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    Requested {when(a.createdAt)}
                    {a.lastLoginAt && ` · last signed in ${when(a.lastLoginAt)}`}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {a.status === "pending" && (
                    <>
                      <button
                        disabled={busy}
                        onClick={() => act(a, "activate")}
                        className="text-sm bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Activate
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => act(a, "decline")}
                        className="text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-gray-700 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Decline
                      </button>
                    </>
                  )}

                  {a.status === "active" && !isSelf && (
                    <>
                      <button
                        disabled={busy}
                        onClick={() =>
                          act(a, a.role === "admin" ? "make-staff" : "make-admin",
                            a.role === "admin"
                              ? `Remove admin rights from ${a.email}?`
                              : `Make ${a.email} an admin? They'll be able to manage accounts and see the activity log.`)
                        }
                        className="text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-gray-700 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        {a.role === "admin" ? "Remove admin" : "Make admin"}
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => act(a, "suspend", `Suspend ${a.email}? They'll be signed out immediately.`)}
                        className="text-sm border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Suspend
                      </button>
                    </>
                  )}

                  {(a.status === "suspended" || a.status === "declined") && (
                    <>
                      <button
                        disabled={busy}
                        onClick={() => act(a, "activate")}
                        className="text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-gray-700 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Reinstate
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => act(a, "delete", `Delete the account for ${a.email}? This can't be undone.`)}
                        className="text-sm border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
